'use client'
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Typography, Box, Grid } from '@mui/material';
import StandardModal from '@/components/modals/StandardModal';
import TextField from '@mui/material/TextField';
import { SPACING } from '@/app/constants/layout-constants';
import { useTodayLocationVisit } from '@/hooks/api/useLocationVisits';
import { StandardUploadInput } from '@/components/form/StandardUploadInput';
import { RESOURCE_TYPE } from '@/stack-shared/constants/resource-constants';
import { useUpsertTrkEvents } from '@/hooks/useLocation';
import { useNotification } from '@/contexts/NotificationContext';
import ReportRejectActionButton from '../reject/ReportRejectActionButton';

export interface ITrkValue {
  id?: number;
  cassette1Load: number;
  cassette2Load: number;
  cassette3Load: number;
  cassette4Load: number;
  locationId: number | undefined;
  assetId: string;
  trkId: number | undefined;
  serviceable: boolean;
  serviceIssue: string;
  balanceAtDeparture: number;
}

interface ILocationVisitTrkFormProps {
  trkValues: ITrkValue[];
  setTrkValues: (value: ITrkValue[]) => void;
  handleDataRefresh: () => void;
  openTrkModal: boolean;
  setOpenTrkModal: (value: boolean) => void;
}

const LocationVisitTrkForm: React.FC<ILocationVisitTrkFormProps> = ({ trkValues, setTrkValues, handleDataRefresh, openTrkModal, setOpenTrkModal }) => {
  const { locationId } = useParams();
  const upsertTrkEventsMutation = useUpsertTrkEvents();
  const { showSuccess, showError } = useNotification();
  const { data: todayVisitData } = useTodayLocationVisit();
  
  const todayVisit = todayVisitData && locationId && Number(todayVisitData.locationId) === Number(locationId) ? todayVisitData : null;

  const handleChange = (index: number, field: keyof ITrkValue, value: string) => {
    const updatedTrkValues = [...trkValues];
    const numValue = value === '' ? 0 : Number(value) || 0;
    updatedTrkValues[index] = {
      ...updatedTrkValues[index],
      [field]: numValue,
    };
    setTrkValues(updatedTrkValues);
    console.log(`TRK ${index} ${field} changed to:`, numValue, 'from input:', value);
  };

  return (
    <StandardModal
      title="Service TRKs"
      open={openTrkModal}
      secondaryAction={<ReportRejectActionButton type="trk" locationIdFilter={Number(locationId)} />}
      maxWidth="xl"
      handleClose={() => setOpenTrkModal(false)}
      action={async () => {
        console.log("=== FRONTEND: Submitting TRK values ===");
        console.log("trkValues:", JSON.stringify(trkValues, null, 2));
        console.log("locationVisitId:", todayVisit?.id);
        console.log("locationId:", locationId);
        
        // Ensure all values are properly formatted
        const trksToSubmit = trkValues.map(trk => ({
          ...trk,
          cassette1Load: String(trk.cassette1Load || 0),
          cassette2Load: String(trk.cassette2Load || 0),
          cassette3Load: String(trk.cassette3Load || 0),
          cassette4Load: String(trk.cassette4Load || 0),
          balanceAtDeparture: String(trk.balanceAtDeparture || 0),
          id: trk.id || trk.trkId,
          trkId: trk.trkId || trk.id,
        }));
        
        console.log("=== FRONTEND: trksToSubmit ===");
        trksToSubmit.forEach((trk, idx) => {
          console.log(`TRK ${idx}:`, {
            id: trk.id,
            trkId: trk.trkId,
            assetId: trk.assetId,
            cassette1Load: trk.cassette1Load,
            cassette2Load: trk.cassette2Load,
            cassette3Load: trk.cassette3Load,
            cassette4Load: trk.cassette4Load,
            balanceAtDeparture: trk.balanceAtDeparture
          });
        });
        
        dispatch(upsertTrkEvents({ 
          trks: trksToSubmit, 
          locationVisitId: todayVisit?.id, 
          locationId: Number(locationId) 
        }, (responseData) => {
          console.log("=== FRONTEND: Save response received ===");
          console.log("responseData:", JSON.stringify(responseData, null, 2));
          console.log("responseData.trkEvents:", responseData?.trkEvents);
          if (responseData?.trkEvents) {
            responseData.trkEvents.forEach((e: any, idx: number) => {
              console.log(`Response TRK Event ${idx}:`, {
                id: e.id,
                trkId: e.trkId,
                locationVisitId: e.locationVisitId,
                cassette1Load: e.cassette1Load,
                cassette2Load: e.cassette2Load,
                cassette3Load: e.cassette3Load,
                cassette4Load: e.cassette4Load,
                balanceAtDeparture: e.balanceAtDeparture
              });
            });
          }
          console.log("Calling handleDataRefresh...");
          handleDataRefresh();
        }));
        setOpenTrkModal(false);
      }}
    >
      <Box style={{ paddingTop: SPACING.md }}>
        <Grid
          container
          spacing={2}
          columns={{ xs: 12, sm: 8 }}
          sx={{
            padding: 1,
            borderBottom: "2px solid #000",
            fontWeight: "bold",
            backgroundColor: "#f5f5f5",
            marginBottom: 1,
            display: {
              xs: 'none',
              sm: 'flex'
            }
          }}
        >
          <Grid size={{ sm: 1, xs: 3 }} sx={{flexGrow:1}}><Typography variant="subtitle1">Asset ID</Typography></Grid>
          <Grid size={{ sm: 1, xs: 3 }} sx={{flexGrow:1}}><Typography variant="subtitle1">Cassette 1 ($1)</Typography></Grid>
          <Grid size={{ sm: 1, xs: 3 }} sx={{flexGrow:1}}><Typography variant="subtitle1">Cassette 2 ($5)</Typography></Grid>
          <Grid size={{ sm: 1, xs: 3 }} sx={{flexGrow:1}}><Typography variant="subtitle1">Cassette 3 ($20)</Typography></Grid>
          <Grid size={{ sm: 1, xs: 3 }} sx={{flexGrow:1}}><Typography variant="subtitle1">Cassette 4 ($100)</Typography></Grid>
          <Grid size={{ sm: 1, xs: 3 }} sx={{flexGrow:1}}><Typography variant="subtitle1">Total Value</Typography></Grid>
          <Grid size={{ sm: 1, xs: 3 }} sx={{flexGrow:1}}><Typography variant="subtitle1">Balance at Departure</Typography></Grid>
        </Grid>
        {trkValues.map((trk, index) => {
          const totalValue = (trk.cassette1Load * 1) + (trk.cassette2Load * 5) + (trk.cassette3Load * 20) + (trk.cassette4Load * 100);
          return (
            <Grid container spacing={1} columns={{ xs: 12, sm: 8}} sx={{ padding: 1, borderBottom: "1px solid #e0e0e0" }}>
              <Grid
                size={{
                  sm: 1,
                  xs: 3
                }}

              >
                <Typography
                  variant="caption"
                  sx={{
                    display: {
                      xs: 'block', // visible on extra-small screens
                      sm: 'none'   // hidden on small screens and above
                    }
                  }}
                >Asset ID</Typography>
                <Typography variant="h6">{trk.assetId}</Typography>
              </Grid>
              {/* <Grid
                size={{
                  sm: 1,
                  xs: 3
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    textAlign: 'center',
                    display: {
                      xs: 'block', // visible on extra-small screens
                      sm: 'none'   // hidden on small screens and above
                    }
                  }}
                >Img</Typography>
                <StandardUploadInput
                  showPreview={true}
                  onFileSelected={(files: any) => console.log("Selected File:", files[0])}
                  accept={'image/*'}
                  uploadType={RESOURCE_TYPE.IMAGE}
                  isThumbnail={true}
                  previewImageStyles={{ width: 50, height: 50 }}
                  handleSubmit={() => console.log("submitting image")}
                />
              </Grid> */}
              <Grid
                size={{ sm: 1, xs: 3 }}

              >
                <TextField type="number" variant="outlined" size="small" label="Cassette 1 ($1)" value={trk.cassette1Load || ''} onChange={(e) => handleChange(index, 'cassette1Load', e.target.value)} />
              </Grid>
              <Grid size={{
                sm: 1,
                xs: 3
              }} ><TextField type="number" variant="outlined" size="small" label="Cassette 2 ($5)" value={trk.cassette2Load || ''} onChange={(e) => handleChange(index, 'cassette2Load', e.target.value)} /></Grid>
              <Grid size={{
                sm: 1,
                xs: 3
              }} ><TextField type="number" variant="outlined" size="small" label="Cassette 3 ($20)" value={trk.cassette3Load || ''} onChange={(e) => handleChange(index, 'cassette3Load', e.target.value)} /></Grid>
              <Grid size={{
                sm: 1,
                xs: 3
              }} ><TextField type="number" variant="outlined" size="small" label="Cassette 4 ($100)" value={trk.cassette4Load || ''} onChange={(e) => handleChange(index, 'cassette4Load', e.target.value)} /></Grid>
              <Grid size={{
                sm: 1,
                xs: 3
              }} >
                <Typography
                  variant="caption"
                  sx={{
                    display: {
                      xs: 'block', // visible on extra-small screens
                      sm: 'none'   // hidden on small screens and above
                    }
                  }}
                >Total Value</Typography>
                <Typography variant="h6">{totalValue}</Typography>
              </Grid>
              <Grid size={{
                sm: 1,
                xs: 3
              }} ><TextField type="number" variant="outlined" size="small" label="Balance at Departure" value={trk.balanceAtDeparture || ''} onChange={(e) => handleChange(index, 'balanceAtDeparture', e.target.value)} /></Grid>
            </Grid>
          );
        })}
      </Box>
    </StandardModal>
  );
};

export default LocationVisitTrkForm;
